import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@radix-ui/themes';
import { Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface Device {
  id: string;
  name: string;
  status: 'online' | 'offline' | 'error';
  lastSeen: string;
  location: string;
}

interface DeviceListProps {
  devices: Device[];
  showViewAll?: boolean;
}

export function DeviceList({ devices, showViewAll = false }: DeviceListProps) {
  // Format the date to be more readable
  const formatLastSeen = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (e) {
      return 'Unknown';
    }
  };

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Last Seen</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No devices found
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device) => (
                <TableRow key={device.id} className="group">
                  <TableCell>
                    <Link
                      to="/dashboard/devices/$deviceId"
                      params={{ deviceId: device.id }}
                      className="text-sm text-muted-foreground font-medium hover:text-primary mt-1 block"
                    >
                      {device.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge color={device.status === 'online' ? 'green' : device.status === 'error' ? 'red' : 'yellow'}>
                      {device.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{device.location}</TableCell>
                  <TableCell>{formatLastSeen(device.lastSeen)}</TableCell>
                  <TableCell>
                    <Link
                      to="/dashboard/devices/$deviceId"
                      params={{ deviceId: device.id }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {showViewAll && devices.length > 0 && (
        <div className="flex justify-end mt-4">
          <Link to="/dashboard/devices">
            <Button variant="outline" size="sm">
              View All Devices
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
